import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router';

import { LazyLoadImage } from 'react-lazy-load-image-component';

import { Title } from '../../utils/title';
import content from '../../utils/content';
import Layout from './Layout';
import styles from './EntryDetail.module.scss';

import hs from '../../assets/brands/hs.png';
import pmp from '../../assets/brands/pmp.png';
import ptt from '../../assets/brands/ptt.png';
import usrx from '../../assets/brands/usrx.png';

const EntryMore = () => {

  const items = [
    {
      label: 'Head and Shoulders',
      image: hs,
      link: '/case-studies/head-shoulders-bare',
    },
    {
      label: 'Pompeian Olive Oil',
      image: pmp,
      link: '/case-studies/pompeian',
    },
    {
      label: 'Pharm to Table',
      image: ptt,
      link: '/case-studies/pharm-to-table',
    },
    {
      label: 'Urban Skin Rx',
      image: usrx,
      link: '/case-studies/urban-skin-rx',
    },
  ];

  return (
    <div className={styles.more}>
      {
        items.map((item, index) => (
          <Link
            key={index}
            to={item.link}
            aria-label={item.label}>

            <div className={styles.image}>
              <LazyLoadImage
                src={item.image}
                alt={item.label}
              />
            </div>

            <div className={styles.label}>
              {item.label}
            </div>

          </Link>
        ))
      }

    </div>
  );
};

const EntryDetail = ({ contentType }) => {

  const { slug } = useParams();

  const [ loading, setLoading ] = useState(false);
  const [ item, setItem ] = useState();

  useEffect(() => {

    setLoading(true);

    const fetchData = contentType === 'blog'
      ? content.getBlogPost(slug)
      : content.getCaseStudy(slug);

    fetchData
      .then(data => {
        setLoading(false);
        setItem(data);
      })
      .catch(err => {
        setLoading(false);
        setItem(null);
      });

  }, [contentType, slug]);

  const renderHtml = (html) => {
    if (!html) return null;
    return <div dangerouslySetInnerHTML={{ __html: html }} />;
  };

  return (
    <Layout>
      <div className={styles.root}>

        {
          loading && (
            <div className={styles.loading}>
              Loading...
            </div>
          )
        }

        {
          item && (
            <>
              <Title value={item.title} />

              <div className={styles.title}>
                {item.title}
              </div>

              <div className={styles.date}>
                {new Date(item.published_date).toLocaleDateString()}
              </div>

              {
                /*
                 * BLOG POST
                 */

                contentType === 'blog' && (
                  <div className={`${styles.content} ${styles.article}`}>
                    <div className={styles.image}>
                      {
                        item.featured_image && (
                          <LazyLoadImage
                            src={item.featured_image}
                            alt={item.featured_image_alt || item.title}
                          />
                        )
                      }
                    </div>
                    {renderHtml(item.content)}
                  </div>
                )
              }

              {
                /*
                 * CASE STUDY
                 */

                contentType === 'case-study' && (
                  <div className={`${styles.content} ${styles.case}`}>
                    <div className={styles.side}>
                      {
                        /*
                         * BRAND LOGO
                         */
                        item.brand_logo && (
                          <LazyLoadImage
                            src={item.brand_logo}
                            alt={item.brand_logo_alt || item.brand}
                          />
                        )
                      }

                      {
                        /*
                         * FEATURED IMAGE
                         */
                        item.featured_image && (
                          <LazyLoadImage
                            src={item.featured_image}
                            alt={item.featured_image_alt || item.title}
                          />
                        )
                      }
                    </div>

                    <div className={styles.main}>
                      {
                        /*
                         * BRAND NAME
                         */
                        item.brand && (
                          <div className={`${styles.section} ${styles.brand}`}>
                            <div className={styles.label}>
                              Brand
                            </div>

                            <div className={styles.value}>
                              {item.brand}
                            </div>
                          </div>
                        )
                      }

                      {
                        /*
                         * AGENCY NAME
                         */
                        item.agency && (
                          <div className={`${styles.section} ${styles.agency}`}>
                            <div className={styles.label}>
                              Agency
                            </div>

                            <div className={styles.value}>
                              {item.agency}
                            </div>
                          </div>
                        )
                      }

                      {
                        /*
                         * MAIN CONTENT
                         */
                        item.main_content && (
                          <div className={`${styles.section} ${styles.content}`}>
                            {renderHtml(item.main_content)}
                          </div>
                        )
                      }

                      {
                        /*
                         * OBJECTIVE
                         */
                        item.objective && (
                          <div className={`${styles.section} ${styles.objective}`}>
                            <div className={styles.label}>
                              Objective
                            </div>

                            <div className={styles.content}>
                              {renderHtml(item.objective)}
                            </div>
                          </div>
                        )
                      }

                      {
                        /*
                         * RESULTS
                         */
                        item.results && (
                          <div className={`${styles.section} ${styles.results}`}>
                            <div className={styles.label}>
                              Results
                            </div>

                            <div className={styles.content}>
                              {renderHtml(item.results)}
                            </div>
                          </div>
                        )
                      }

                      {
                        /*
                         * STATS
                         */
                        item.stats && (item.stats.data || item.stats).length > 0 && (
                          <div className={`${styles.section} ${styles.stats}`}>
                            <table>
                              <tbody>
                                {
                                  (item.stats.data || item.stats).map((stat, index) => (
                                    <tr key={index}>
                                      <td>{stat.label}</td>
                                      <td>{stat.value}</td>
                                    </tr>
                                  ))
                                }
                              </tbody>
                            </table>
                          </div>
                        )
                      }

                      {
                        /*
                         * SAMPLE CONTENT
                         */
                        item.sample_content &&
                        item.sample_content.length > 0 && (
                          <div className={`${styles.section} ${styles.samples}`}>
                            <div className={styles.label}>
                              Sample Content
                            </div>

                            <div className={styles.items}>
                              {
                                item.sample_content.map((media, index) => (
                                  <div key={index}>
                                    {
                                      media.type === 'video' ? (
                                        <video
                                          src={media.url + '#t=0.1'}
                                          controls
                                          playsInline
                                          preload="metadata"
                                          style={{ width: '100%', maxWidth: '400px' }}
                                        />
                                      ) : (
                                        <LazyLoadImage
                                          src={media.url}
                                          alt={media.title}
                                        />
                                      )
                                    }
                                  </div>
                                ))
                              }
                            </div>
                          </div>
                        )
                      }

                      {
                        /*
                         * ACTION
                         */
                        (
                          <div className={`${styles.section} ${styles.action}`}>
                            <Link to='?dialog=contact'>
                              Get Reviews
                            </Link>
                          </div>
                        )
                      }
                    </div>
                  </div>
                )
              }
            </>
          )
        }

        {
          contentType === 'case-study' && item && (
            <EntryMore />
          )
        }

      </div>
    </Layout>
  );
};

export default EntryDetail;
